// ── the private ring · the pure half ─────────────────────────────────────────
//
// Which disclosure classes of a person's memory may leave to one app, right
// now. No I/O here: lib/self/access.ts reads the app's settings and windows and
// hands them to `decideRelease`; the handlers ask it before they select a row.
//
// The classes, most private first (lib/you/private.ts DISCLOSURE_ORDER):
//
//   private < self < trusted < work < public
//
// An app's DEPTH (migration 128, `connections` / `api_keys`):
//
//   standard  trusted · work · public       the default, and the behaviour
//                                           before the ring existed
//   personal  + self
//   private   + self + private
//
// `ask_first` (default true) holds anything beyond standard back until the
// person allows it. Since 149 an allowance is a GRANT that can end: for 30
// minutes, a day, a week, or always, and optionally narrowed to a few
// categories of personal memory (lib/self/categories.ts) instead of a whole
// depth. "always" for a whole depth raises the app's depth and stops it asking,
// as before; every other grant is a row of its own, and a fact leaves when ANY
// grant the app holds covers its class and its category. Nothing about an
// unknown app is ever guessed: no app, no settings, no grant → standard.
//
// What an app may WRITE is its write mode (149): it writes freely (the default,
// and the behaviour before), it asks first (what it would write waits for the
// person's yes, lib/self/writes.ts), or it is read only.

import { anySensitive, categoryOf, type Category } from './categories'
import { unionKinds } from './kinds'

export type Tier = 'private' | 'self' | 'trusted' | 'work' | 'public'
export type Depth = 'standard' | 'personal' | 'private'
export type RevealDecision = 'allowed' | 'once' | 'approved'
/** What the reveal ledger can say of a row: how a fact was cleared to leave, or
 *  (migration 129, lib/self/abstract.ts) that its words stayed and netti only
 *  shaped a hint from it. */
export type LedgerDecision = RevealDecision | 'abstracted'

export const TIERS: readonly Tier[] = ['private', 'self', 'trusted', 'work', 'public']
export const DEPTHS: readonly Depth[] = ['standard', 'personal', 'private']
/** What every app may read, always: the ring's floor. */
export const STANDARD_TIERS: readonly Tier[] = ['trusted', 'work', 'public']
/** The two classes the ring guards. */
export const RING_TIERS: readonly Tier[] = ['private', 'self']

/** An allow-once window lasts this long (and the SQL agrees: 128). */
export const ONCE_WINDOW_MS = 30 * 60_000

/** How long a grant lasts, as the person chooses it (149). */
export type GrantLength = 'once' | 'day' | 'week' | 'always'
export const GRANT_LENGTHS: readonly GrantLength[] = ['once', 'day', 'week', 'always']
/** The minutes each length that ends lasts (the SQL accepts exactly these: 149). */
export const GRANT_MINUTES: Record<Exclude<GrantLength, 'always'>, number> = { once: 30, day: 1440, week: 10080 }
export const isGrantLength = (v: unknown): v is GrantLength => typeof v === 'string' && (GRANT_LENGTHS as readonly string[]).includes(v)
/** The minutes a length lasts; null for always. */
export const minutesOf = (length: GrantLength): number | null => (length === 'always' ? null : GRANT_MINUTES[length])

/**
 * The length a grant starts at when the person has not chosen one: every grant
 * ends unless they say always. A day when it reaches a sensitive category (or a
 * whole depth, which holds them), a week otherwise. Never always.
 */
export function defaultLength(categories: readonly Category[] | null | undefined): GrantLength {
  if (!categories || !categories.length) return 'day'
  return anySensitive(categories) ? 'day' : 'week'
}

/** What an app may write (149). */
export type WriteMode = 'free' | 'ask' | 'read_only'
export const WRITE_MODES: readonly WriteMode[] = ['free', 'ask', 'read_only']
export const isWriteMode = (v: unknown): v is WriteMode => typeof v === 'string' && (WRITE_MODES as readonly string[]).includes(v)
/** Anything unknown writes as every app did before 149. */
export const normWriteMode = (v: unknown): WriteMode => (isWriteMode(v) ? v : 'free')
/** After a deny, the same app does not raise a new request for this long. */
export const DENY_QUIET_MS = 24 * 60 * 60_000
/** A request nobody answered goes stale after this long. */
export const REQUEST_STALE_MS = 7 * 24 * 60 * 60_000

export const isTier = (v: unknown): v is Tier => typeof v === 'string' && (TIERS as readonly string[]).includes(v)
export const isDepth = (v: unknown): v is Depth => typeof v === 'string' && (DEPTHS as readonly string[]).includes(v)
export const normDepth = (v: unknown): Depth => (isDepth(v) ? v : 'standard')

export function depthRank(d: Depth): number {
  return d === 'private' ? 2 : d === 'personal' ? 1 : 0
}

export function maxDepth(a: Depth, b: Depth): Depth {
  return depthRank(a) >= depthRank(b) ? a : b
}

/** The tiers a depth reaches. */
export function tiersFor(depth: Depth): Tier[] {
  if (depth === 'private') return ['private', 'self', ...STANDARD_TIERS]
  if (depth === 'personal') return ['self', ...STANDARD_TIERS]
  return [...STANDARD_TIERS]
}

/** The depth a tier needs. */
export function depthForTier(tier: Tier): Depth {
  return tier === 'private' ? 'private' : tier === 'self' ? 'personal' : 'standard'
}

/** The deepest depth any of these tiers needs (standard for none). */
export function depthNeeded(tiers: readonly Tier[]): Depth {
  return tiers.reduce<Depth>((d, t) => maxDepth(d, depthForTier(t)), 'standard')
}

/**
 * The tiers a `max_disclosure` asks for: that class and every class less
 * private than it (`trusted` → trusted, work, public; `private` → all five).
 */
export function tiersUpTo(max: Tier): Tier[] {
  return TIERS.slice(TIERS.indexOf(max))
}

/** Who is calling, as far as the door could tell. */
export interface AppRef {
  /** `owner` is the person themselves, in their own signed-in session (the
   *  dashboard) — never an app, never ledgered. Only owner-session routes
   *  build one (lib/self/access ownerDoor). */
  kind: 'connection' | 'api_key' | 'none' | 'owner'
  id: string | null
  /** the person's own name for it (a connection's label, a key's label) */
  label: string | null
  /** the client on the other end (clientInfo / oauth client name) */
  client: string | null
}

/**
 * One allowance the person gave an app beyond its standing depth: a window
 * that ends (allowed once, for a day, for a week) or a standing grant narrowed
 * to some categories (`until` null). `categories` null = the whole depth.
 */
export interface Grant {
  requestId: string
  depth: Exclude<Depth, 'standard'>
  categories: Category[] | null
  /** when it ends; null = it does not end on its own (always, narrowed) */
  until: string | null
}

/** What one app may read, as its settings and its decided requests say. */
export interface AppGrant {
  app: AppRef
  depth: Depth
  askFirst: boolean
  /** when the depth last changed (connections.personal_depth_at) */
  depthSetAt: string | null
  /** the grants it holds now (ended ones are never read) */
  windows: Grant[]
  /** when this app's most recent whole-depth "always" was decided */
  approvedAt: string | null
  /** when this app was last denied */
  deniedAt: string | null
  /** what it may write (149; 'free' before, and for an app we cannot place) */
  writeMode: WriteMode
}

/** The grant of an app nobody knows anything about: standard, and nothing more. */
export function standardGrant(app: AppRef = { kind: 'none', id: null, label: null, client: null }): AppGrant {
  return { app, depth: 'standard', askFirst: true, depthSetAt: null, windows: [], approvedAt: null, deniedAt: null, writeMode: 'free' }
}

/** A grant that is still live at `now`. */
export const grantLive = (g: Pick<Grant, 'until'>, now: number = Date.now()): boolean => g.until == null || Date.parse(g.until) > now

export interface Release {
  /** requested tiers that may leave now, every fact of them */
  cleared: Tier[]
  /** requested tiers held back — wholly, or (see `partial`) outside some categories */
  withheld: Tier[]
  /**
   * Ring tiers cleared only for some categories (a narrowed grant). Such a tier
   * stays in `withheld` (a reader that does not know categories holds the whole
   * tier back, never too much out); a reader that does lets a fact of it leave
   * when `clears()` says so.
   */
  partial: Partial<Record<Tier, Category[]>>
  /** why each withheld tier was held back */
  why: Partial<Record<Tier, 'depth' | 'ask_first'>>
  /** how each cleared ring tier (self/private) was cleared, for the ledger */
  via: Partial<Record<Tier, RevealDecision>>
  /** whether a withheld read may open (or refresh) an access request */
  askable: boolean
  /** the depth the withheld tiers need (standard when nothing was held back) */
  needs: Depth
  grant: AppGrant
}

/**
 * Decide which of the requested tiers may leave to this app now.
 *
 * `requested` omitted means "what this app is currently allowed to see": its
 * depth, widened by an open window. That keeps a standard app with no window
 * exactly where every app was before the ring: trusted, work, public.
 */
export function decideRelease(grant: AppGrant, requested?: readonly Tier[] | null, now: number = Date.now()): Release {
  if (grant.app.kind === 'owner') {
    // the person reading their own memory: everything asked for, nothing to ledger
    const all = requested && requested.length ? TIERS.filter((t) => requested.includes(t)) : [...TIERS]
    return { cleared: all, withheld: [], partial: {}, why: {}, via: {}, askable: false, needs: 'standard', grant }
  }
  const live = (grant.windows ?? []).filter((g) => grantLive(g, now))
  const standing = tiersFor(grant.depth)
  const reach = live.reduce<Depth>((d, g) => maxDepth(d, g.depth), grant.depth)
  const ask = requested && requested.length ? [...new Set(requested)].filter(isTier) : tiersFor(reach)

  const approved =
    !!grant.approvedAt && !!grant.depthSetAt && Date.parse(grant.approvedAt) >= Date.parse(grant.depthSetAt) - 1000

  const cleared: Tier[] = []
  const withheld: Tier[] = []
  const partial: Release['partial'] = {}
  const why: Release['why'] = {}
  const via: Release['via'] = {}
  for (const t of TIERS) {
    if (!ask.includes(t)) continue
    if ((STANDARD_TIERS as readonly Tier[]).includes(t)) {
      cleared.push(t)
      continue
    }
    // a ring tier: the standing depth (when the app does not ask first), else a
    // grant that reaches it — a whole one clears the tier, narrowed ones clear
    // the union of their categories and hold the rest back
    const grants = live.filter((g) => tiersFor(g.depth).includes(t))
    const whole = grants.find((g) => !g.categories || !g.categories.length)
    if (standing.includes(t) && !grant.askFirst) {
      cleared.push(t)
      via[t] = approved ? 'approved' : 'allowed'
    } else if (whole) {
      cleared.push(t)
      via[t] = 'once'
    } else {
      withheld.push(t)
      why[t] = standing.includes(t) ? 'ask_first' : 'depth'
      const cats = [...new Set(grants.flatMap((g) => g.categories ?? []))]
      if (cats.length) {
        partial[t] = cats
        via[t] = 'once'
      }
    }
  }

  const deniedRecently = !!grant.deniedAt && now - Date.parse(grant.deniedAt) < DENY_QUIET_MS
  return {
    cleared,
    withheld,
    partial,
    why,
    via,
    askable: withheld.length > 0 && grant.app.kind !== 'none' && !!grant.app.id && !deniedRecently,
    needs: depthNeeded(withheld),
    grant,
  }
}

/**
 * Whether ONE fact may leave: its class is cleared, or its class is cleared
 * for some categories and its dimension (and every other kind it is, 158)
 * falls in one of them. The one test
 * every reader that knows about categories applies to every row.
 */
export function clears(
  release: Pick<Release, 'cleared' | 'partial'>,
  tier: unknown,
  dimension: string | null | undefined,
  kinds?: unknown,
): boolean {
  if (!isTier(tier)) return false
  if (release.cleared.includes(tier)) return true
  const cats = release.partial[tier]
  if (!cats || !cats.length) return false
  // one fact, many kinds (158): a narrowed grant lets a fact out only when EVERY kind it is falls in a
  // granted category, so a preference that is also about health never passes a grant for taste alone
  const all = Array.isArray(kinds) && kinds.length ? unionKinds(String(dimension ?? ''), kinds) : [dimension]
  return all.every((d) => {
    const c = categoryOf(d)
    return !!c && cats.includes(c)
  })
}

/** The tiers a reader selects rows from: cleared ones and partly cleared ones (each row then passes `clears`). */
export function readableTiers(release: Pick<Release, 'cleared' | 'partial'>): Tier[] {
  return TIERS.filter((t) => release.cleared.includes(t) || !!release.partial[t]?.length)
}

/** Clip an ask for the ledger: one line, at most `max` characters. */
export function clipAsk(ask: string | null | undefined, max = 160): string {
  const one = String(ask ?? '').replace(/\s+/g, ' ').trim()
  return one.length <= max ? one : `${one.slice(0, max - 1).trimEnd()}…`
}

/** Where the person answers an app's request: the you space's private view
 *  (the apex has no /you; /dashboard/you carries the person to their own
 *  dashboard host, and #ring rides the redirect). */
export const RING_URL = 'https://innernet.live/dashboard/you#ring'

/**
 * What a response says about facts it held back. Counts and classes only —
 * never a word of a withheld fact, never which dimensions they sit in.
 */
export function withheldNote(
  release: Pick<Release, 'withheld' | 'why' | 'askable'>,
  count: number,
  opts: { requested: boolean; denied?: boolean } = { requested: false },
): string {
  if (count <= 0) return ''
  const what = release.withheld.includes('private') ? (release.withheld.includes('self') ? 'self or private' : 'private') : 'self'
  const n = `${count} ${what} fact${count === 1 ? '' : 's'}`
  const asks = release.withheld.some((t) => release.why[t] === 'ask_first')
  if (opts.requested) {
    return `${n} ${count === 1 ? 'was' : 'were'} held back: this app ${asks ? 'asks first' : 'is not cleared for them'}. innernet asked the person; they can allow it for a while, always, or not at ${RING_URL}. do not ask them to paste these facts into the chat.`
  }
  if (opts.denied) {
    return `${n} ${count === 1 ? 'was' : 'were'} held back: the person chose not to share these with this app for now.`
  }
  return `${n} ${count === 1 ? 'was' : 'were'} held back: this app is set to ${asks ? 'ask first' : 'standard'}. the person decides what it may read at ${RING_URL}.`
}

/** The terms an ask is made of, for a cheap "does this fact bear on it" test. */
export function bearsOn(statement: string, askTerms: ReadonlySet<string>, terms: (s: string) => string[]): boolean {
  if (!askTerms.size) return false
  return terms(statement).some((t) => askTerms.has(t))
}
